import * as THREE from 'three';
import type { MapDefinition, Point } from '../../../shared/game/world-types.js';
import { seededRandom } from './scenery-batches.js';
import type { AnimatedProp } from './coast-props.js';
import { animateTransform } from '../scene-transforms.js';
import { withFogOfWar } from './fog-of-war.js';
import { conditions, nightUniform } from './conditions.js';

/** Soft round puff generated in memory, so it works without a DOM canvas. */
export function softTexture() {
  const size = 32, data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) {
    const d = Math.hypot(x - size / 2 + .5, y - size / 2 + .5) / (size / 2), a = Math.max(0, 1 - d), i = (y * size + x) * 4;
    data.set([255, 255, 255, Math.round(255 * a * a)], i);
  }
  // Filtered, so a halo stretched over metres stays a smooth falloff instead of a grid of texels.
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat); texture.magFilter = texture.minFilter = THREE.LinearFilter; texture.needsUpdate = true; return texture;
}

/**
 * Drifting cloud shadows, wind-borne motes, chimney/campfire smoke and cave bats.
 * Everything is driven by `time`, so pausing the game freezes the weather too.
 */
export function createAtmosphere(scene: THREE.Scene, map: MapDefinition, smokeSources: (Point & { height: number; scale: number })[]): AnimatedProp {
  const scenery = map.scenery!, random = seededRandom(scenery.seed ^ 0x77ad);

  // Cloud shadows slide across the land and break up the flat ground colour.
  const cloudMaterial = new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, cover: { value: .4 } }, transparent: true, depthWrite: false,
    vertexShader: 'varying vec2 g; void main(){ g = position.xz; gl_Position = projectionMatrix*modelViewMatrix*vec4(position,1.); }',
    fragmentShader: `varying vec2 g; uniform float time; uniform float cover;
      float h(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
      float n(vec2 p){ vec2 i=floor(p), f=fract(p); f=f*f*(3.-2.*f); return mix(mix(h(i),h(i+vec2(1.,0.)),f.x),mix(h(i+vec2(0.,1.)),h(i+vec2(1.,1.)),f.x),f.y); }
      float fbm(vec2 p){ float v=0., a=.5; for(int i=0;i<4;i++){ v+=a*n(p); p=mat2(.8,.6,-.6,.8)*p*2.02+vec2(11.7,3.1); a*=.5; } return v; }
      void main(){
        vec2 p = g*.0042 + vec2(time*.0021, time*.0009);
        float c = fbm(p);
        float shade = smoothstep(.44 - .18*cover,.8 - .12*cover,c);
        gl_FragColor = vec4(.015,.01,.05, shade*(.3 + .3*cover));
        #include <colorspace_fragment>
      }`,
  });
  const clouds = new THREE.Mesh(new THREE.PlaneGeometry(map.width + 300, map.depth + 300).rotateX(-Math.PI / 2), cloudMaterial);
  clouds.position.y = .12; clouds.renderOrder = 2; clouds.frustumCulled = false; scene.add(clouds);

  // Motes: pollen and embers by day's end, fireflies in marsh and pine, concentrated by district.
  const count = 3200, positions = new Float32Array(count * 3), seeds = new Float32Array(count), tints = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    const district = random() < .75 ? scenery.districts[Math.floor(random() * scenery.districts.length)] : undefined;
    const angle = random() * Math.PI * 2, r = district ? Math.sqrt(random()) * district.radius : 0;
    const x = district ? district.x + Math.cos(angle) * r : (random() - .5) * map.width, z = district ? district.z + Math.sin(angle) * r : (random() - .5) * map.depth;
    positions.set([Math.max(-map.width / 2, Math.min(map.width / 2, x)), .4 + random() * 4.5, Math.max(-map.depth / 2, Math.min(map.depth / 2, z))], i * 3);
    seeds[i] = random() * 100;
    const firefly = district && (district.biome === 'marsh' || district.biome === 'pine') && random() < .09;
    tints.set(firefly ? [.75, 1, .45] : [1, .74, .42], i * 3);
  }
  const moteGeometry = new THREE.BufferGeometry();
  moteGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  moteGeometry.setAttribute('seed', new THREE.BufferAttribute(seeds, 1)); moteGeometry.setAttribute('tint', new THREE.BufferAttribute(tints, 3));
  const moteMaterial = withFogOfWar(new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, night: nightUniform }, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    vertexShader: `attribute float seed; attribute vec3 tint; uniform float time; uniform float night; varying float a; varying vec3 c;
      void main(){ vec3 p = position; float t = time*.25 + seed;
        p.x += sin(t*1.3 + seed)*3.2 + time*.35; p.z += cos(t*1.1 + seed*2.)*3.2; p.y += sin(t*1.7 + seed*3.)*.7;
        // Fireflies (the green motes) come out at night and blink on and off; pollen and embers belong to the daylight.
        float firefly = step(tint.x, .8);
        a = (.25 + .75*pow(.5+.5*sin(time*(1.2+fract(seed)*1.5)+seed*7.), 3.)) * mix(1. - .9*night, night, firefly); c = tint;
        gl_PointSize = (2.2 + 2.2*fract(seed*3.7)) * (1. + .25*firefly*night);
        gl_Position = projectionMatrix*modelViewMatrix*vec4(p,1.); }`,
    fragmentShader: `varying float a; varying vec3 c; void main(){ float d = length(gl_PointCoord-.5)*2.; if(d>1.) discard; gl_FragColor = vec4(c, a*(1.-d*d)); }`,
  }));
  const motes = new THREE.Points(moteGeometry, moteMaterial); motes.frustumCulled = false; scene.add(motes);

  // Smoke: a stack of soft sprites per source, rising and fanning downwind (north-east, away from the sun).
  const puffTexture = softTexture(), puffs: { sprite: THREE.Sprite; source: Point & { height: number; scale: number }; phase: number; material: THREE.SpriteMaterial }[] = [];
  for (const source of smokeSources) for (let i = 0; i < 9; i++) {
    const material = new THREE.SpriteMaterial({ map: puffTexture, color: 0x8b7885, transparent: true, depthWrite: false, opacity: 0 });
    const sprite = animateTransform(new THREE.Sprite(material)); scene.add(sprite); puffs.push({ sprite, source, phase: i / 9 + random() * .05, material });
  }

  // Bats wheel under the cave's open roof, restless in the crystal light.
  const bats: { group: THREE.Group; wings: THREE.Mesh[]; phase: number; radius: number }[] = [];
  const batMaterial = new THREE.MeshBasicMaterial({ color: 0x1b1520, side: THREE.DoubleSide });
  for (let i = 0; i < 9; i++) {
    const group = animateTransform(new THREE.Group()), wings: THREE.Mesh[] = [];
    for (const side of [-1, 1]) {
      const geometry = new THREE.ConeGeometry(.14, .75, 3); geometry.rotateZ(Math.PI / 2); geometry.translate(side * .3, 0, 0);
      const wing = animateTransform(new THREE.Mesh(geometry, batMaterial)); group.add(wing); wings.push(wing);
    }
    scene.add(group); bats.push({ group, wings, phase: random() * 6.28, radius: 7 + random() * 18 });
  }
  const caveCenter = { x: 345, z: -270 };

  return { update: time => {
    cloudMaterial.uniforms.time.value = moteMaterial.uniforms.time.value = time; cloudMaterial.uniforms.cover.value = conditions.cover;
    for (const puff of puffs) {
      const t = (time * .09 + puff.phase) % 1, { source } = puff;
      puff.sprite.position.set(source.x + t * t * 9 + Math.sin(time * .6 + puff.phase * 9) * .4 * t, source.height + t * 13 * source.scale, source.z - t * t * 5);
      const size = (1.1 + t * 4.2) * source.scale; puff.sprite.scale.set(size, size, 1);
      puff.material.opacity = Math.sin(Math.min(t * 1.25, 1) * Math.PI) * .34 * Math.min(1, source.scale + .3);
    }
    for (const bat of bats) {
      const a = time * (.55 + bat.phase * .04) + bat.phase, r = bat.radius * (.7 + .3 * Math.sin(time * .2 + bat.phase));
      bat.group.position.set(caveCenter.x + Math.cos(a) * r * 1.3, 7 + Math.sin(a * 2.3 + bat.phase) * 2.6 + (bat.phase % 3), caveCenter.z - 5 + Math.sin(a) * r * .55);
      bat.group.rotation.y = -a - Math.PI / 2; bat.group.rotation.z = Math.sin(a * 2.3) * .2;
      bat.wings.forEach((wing, side) => { wing.rotation.z = Math.sin(time * 14 + bat.phase) * .8 * (side ? -1 : 1); });
    }
  } };
}
